import { Base } from '@studiometa/js-toolkit';

/**
 * @param {HTMLElement} el1
 * @param {HTMLElement} el2
 * @returns {boolean}
 */
function sorter(el1, el2) {
  const el1Size = el1.getBoundingClientRect();
  const el2Size = el2.getBoundingClientRect();
  const pos1 = Math.abs(el1Size.top + el1Size.height / 2 - window.innerHeight / 2);
  const pos2 = Math.abs(el2Size.top + el2Size.height / 2 - window.innerHeight / 2);
  return pos1 - pos2;
}

/**
 * AnchorNav component
 */
export default class AnchorNav extends Base {
  /**
   * Component Configuration
   */
  static config = {
    name: 'AnchorNav',
    options: {
      contentSelector: String,
      withTemplate: Boolean,
    },
    refs: ['list', 'itemTemplate'],
    services: {
      scroll: true,
    },
  };

  /**
   * Component mounted hook
   */
  mounted() {
    this.elementsArray = [];
    this.links = [];

    setTimeout(() => {
      if (!this.$options.contentSelector || this.$options.contentSelector.trim() === '') {
        return;
      }

      if (this.$options.withTemplate) {
        this.generateAnchors();
      }

      this.elements = document.querySelectorAll(this.$options.contentSelector);
      this.links = this.$el.querySelectorAll('li a');
      this.elementsArray = [];

      this.elements.forEach((element) => {
        if (!element.hasAttribute('tabindex')) {
          element.setAttribute('tabindex', '-1');
        }
        this.elementsArray.push(element);
      });

      this.links.forEach((link) => link.addEventListener('click', this.onLinkClick.bind(this)));
    }, 1);
  }

  /**
   * Move accessibility focus to the target heading after the native
   * hash-scroll happens, since scrolling alone does not move keyboard focus.
   * @param {MouseEvent} event
   */
  onLinkClick(event) {
    const href = event.currentTarget.getAttribute('href');
    if (!href || href.length < 2) {
      return;
    }

    // Ids generated by generateUniqueIdBase() are slugified from arbitrary
    // heading text and can start with a digit (e.g. "2024 en chiffres" ->
    // "2024-en-chiffres"), which is an invalid CSS identifier and throws a
    // SyntaxError from querySelector. getElementById has no such
    // restriction.
    // eslint-disable-next-line unicorn/prefer-query-selector -- see comment above
    const target = document.getElementById(href.slice(1));
    if (!target) {
      return;
    }

    target.focus({ preventScroll: true });
  }

  /**
   * Service scrolled hook
   */
  scrolled() {
    if (!this.elementsArray || this.elementsArray.length === 0) {
      return;
    }

    this.elementsArray.sort(sorter);

    if (this.links) {
      this.links.forEach((element) => {
        element.classList.remove('bg-grey-1');
      });
    }

    // Check if the first element exists and has an
    const firstElement = this.elementsArray[0];
    if (firstElement && firstElement.id) {
      const targetLink = document.querySelector(`[href='#${firstElement.id}']`);
      if (targetLink) {
        targetLink.classList.add('bg-grey-1');
      }
    }
  }
  /**
   * Generate navigation anchors from template
   */
  generateAnchors() {
    if (
      !this.$refs.itemTemplate ||
      !this.$refs.list ||
      !this.$options.contentSelector ||
      this.$options.contentSelector.trim() === ''
    ) {
      return;
    }

    document.querySelectorAll(this.$options.contentSelector).forEach((section) => {
      if (!section.id) {
        return;
      }

      const autoAnchor = section.querySelector('a.auto-anchor');
      let autoAnchorText = '';
      if (autoAnchor) {
        autoAnchorText = autoAnchor.textContent;
      }

      const tpl = document.createElement('div');
      tpl.innerHTML = this.$refs.itemTemplate.innerHTML;
      const li = tpl.querySelector('li');
      const anchor = li.querySelector('a');

      if (anchor && li) {
        anchor.href = `#${section.id}`;
        anchor.innerHTML = section.textContent.replace(autoAnchorText, '').trim();
        this.$refs.list.append(li);
      }
    });
  }
}
